/**
 * Mobile App Page - Installation instructions for Android app
 * Three installation methods: Play Store, APK Production Installation (kiosk), APK Test Installation
 */

import { Card, Typography, Collapse, Steps, Alert, Button, Space, Divider, Row, Col } from 'antd';
import { AndroidOutlined, GoogleOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import AppLayout from '@/components/AppLayout';
import { MaterialIcon } from '@/components/MaterialIcon';

const { Title, Text, Paragraph } = Typography;

// OTA APK download URL (Firebase Storage)
const OTA_APK_URL = `https://firebasestorage.googleapis.com/v0/b/${import.meta.env.VITE_FIREBASE_STORAGE_BUCKET}/o/releases%2Fmasterlapse-latest.apk?alt=media`;

// ADB installation instructions by OS
const adbInstallItems = [
  {
    key: 'macos',
    label: <Space><MaterialIcon name="laptop_mac" /> macOS</Space>,
    children: (
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: <Text strong style={{ fontSize: 16 }}>Install Homebrew (if not installed)</Text>,
            description: (
              <Space direction="vertical">
                <Text>Open Terminal and run:</Text>
                <Text code copyable>/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"</Text>
              </Space>
            ),
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Install ADB via Homebrew</Text>,
            description: <Text code copyable>brew install android-platform-tools</Text>,
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Verify installation</Text>,
            description: (
              <Space direction="vertical">
                <Text code copyable>adb version</Text>
                <Text type="secondary">Should show "Android Debug Bridge version..."</Text>
              </Space>
            ),
          },
        ]}
      />
    ),
  },
  {
    key: 'windows',
    label: <Space><MaterialIcon name="desktop_windows" /> Windows</Space>,
    children: (
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: <Text strong style={{ fontSize: 16 }}>Download Platform Tools</Text>,
            description: (
              <Space direction="vertical">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href="https://dl.google.com/android/repository/platform-tools-latest-windows.zip"
                  target="_blank"
                >
                  Download Platform Tools (Windows)
                </Button>
              </Space>
            ),
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Extract the ZIP</Text>,
            description: 'Extract to a folder like C:\\platform-tools',
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Add to PATH (optional but recommended)</Text>,
            description: (
              <Space direction="vertical">
                <Text>Open System Properties → Environment Variables → Edit PATH → Add the folder path</Text>
                <Text type="secondary">Or navigate to the folder each time you use ADB</Text>
              </Space>
            ),
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Open Command Prompt and verify</Text>,
            description: (
              <Space direction="vertical">
                <Text code copyable>adb version</Text>
                <Text type="secondary">Should show "Android Debug Bridge version..."</Text>
              </Space>
            ),
          },
        ]}
      />
    ),
  },
  {
    key: 'linux',
    label: <Space><MaterialIcon name="terminal" /> Linux</Space>,
    children: (
      <Steps
        direction="vertical"
        size="small"
        current={-1}
        items={[
          {
            title: <Text strong style={{ fontSize: 16 }}>Install via package manager</Text>,
            description: (
              <Space direction="vertical">
                <Text>Ubuntu/Debian:</Text>
                <Text code copyable>sudo apt install android-tools-adb</Text>
                <Text style={{ marginTop: 8 }}>Fedora:</Text>
                <Text code copyable>sudo dnf install android-tools</Text>
              </Space>
            ),
          },
          {
            title: <Text strong style={{ fontSize: 16 }}>Verify installation</Text>,
            description: <Text code copyable>adb version</Text>,
          },
        ]}
      />
    ),
  },
];

export default function MobileAppPage() {
  // Nested OTA options (for devices without Google account)
  const otaSubItems = [
    {
      key: 'ota-kiosk-mode',
      label: (
        <Space>
          <MaterialIcon name="lock" />
          <><Text strong>Production Installation</Text> (10 minutes)</>
        </Space>
      ),
      children: (
        <div>
          {/* ADB Setup Section */}
          <Title level={5} style={{ marginTop: 0 }}>
            <MaterialIcon name="usb" style={{ marginRight: 8 }} />
            Step 1: Install ADB on Your Computer
          </Title>
          <Paragraph>
            ADB (Android Debug Bridge) is a command-line tool to communicate with Android devices via USB.
          </Paragraph>

          <Collapse
            accordion
            items={adbInstallItems}
            style={{ marginBottom: 24 }}
          />

          {/* Set Kiosk Mode Section */}
          <Title level={5}>
            <MaterialIcon name="lock" style={{ marginRight: 8 }} />
            Step 2: Enable Kiosk Mode
          </Title>

          <Alert
            type="warning"
            showIcon
            message="Factory reset required"
            description="The procedure below requires Factory Reset, this will erase all data on the device. Make sure to backup any important data before proceeding."
            style={{ marginBottom: 16 }}
          />

          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={[
              {
                title: <Text strong style={{ fontSize: 16 }}>Prepare for APK installation</Text>,
                description: (
                  <Text>You will install the APK directly via ADB in a later step (no manual download needed).</Text>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Factory reset the device</Text>,
                description: 'Go to Settings > System > Reset > Factory data reset. Wait for the device to restart.',
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Complete initial setup</Text>,
                description: (
                  <Space direction="vertical">
                    <Text><Text strong type="danger">IMPORTANT:</Text> Skip Google account login - Kiosk Mode cannot be enabled if any account exists</Text>
                    <Text><Text strong type="danger">IMPORTANT:</Text> Skip PIN/pattern lock setup - avoid any screen lock</Text>
                  </Space>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Disable System Updates and enable USB Debugging</Text>,
                description: (
                  <Space direction="vertical">
                    <Text>Disable <Text strong>Settings → System → System update</Text></Text>
                    <Text>Go to <Text strong>Settings → About phone</Text>, find <Text strong>Build number</Text> and tap it 7 times</Text>
                    <Text>Go to <Text strong>Settings → System → Developer options</Text>, enable <Text strong>USB Debugging</Text></Text>
                  </Space>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Connect USB and authorize</Text>,
                description: (
                  <Space direction="vertical">
                    <Text>Connect the device to your computer via USB</Text>
                    <Text>Tap <Text strong>Allow</Text> on the <Text strong>"Allow USB debugging?"</Text> dialog, selecting <Text strong>"Always allow"</Text> and <Text strong>"File transfer"</Text> options if present</Text>
                  </Space>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Install APK and enable Kiosk Mode</Text>,
                description: (
                  <Space direction="vertical">
                    <Text>Run these commands to download and install the APK directly:</Text>
                    <Collapse
                      size="small"
                      items={[
                        {
                          key: 'macos-linux',
                          label: <Text>macOS / Linux</Text>,
                          children: (
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Text code copyable>{`curl -L "${OTA_APK_URL}" -o /tmp/masterlapse.apk && adb install /tmp/masterlapse.apk`}</Text>
                            </Space>
                          ),
                        },
                        {
                          key: 'windows',
                          label: <Text>Windows (PowerShell)</Text>,
                          children: (
                            <Space direction="vertical" style={{ width: '100%' }}>
                              <Text code copyable>{`Invoke-WebRequest -Uri "${OTA_APK_URL}" -OutFile "$env:TEMP\\masterlapse.apk"; adb install "$env:TEMP\\masterlapse.apk"`}</Text>
                            </Space>
                          ),
                        },
                      ]}
                      style={{ marginBottom: 8 }}
                    />
                    <Text>Then enable Kiosk Mode:</Text>
                    <Text code copyable>adb shell dpm set-device-owner com.masterlapse.app/.receiver.TimelapseDeviceAdminReceiver --skip-profile-owner-check</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Note: If you see "device owner is already set" error, Kiosk Mode is already enabled - you can skip this step.
                    </Text>
                  </Space>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Launch the MasterLapse app</Text>,
                description: 'Launch the MasterLapse app, it will appear in Kiosk Mode.',
              },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'ota-manual',
      label: (
        <Space>
          <MaterialIcon name="lock_open" />
          <><Text strong>Test Installation</Text> (3 minutes)</>
        </Space>
      ),
      children: (
        <div>
          <Paragraph>
            Direct APK installation without kiosk lock (Device Owner). Updates will show a confirmation dialog
            that requires someone to tap "Install" on the device.
          </Paragraph>

          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={[
              {
                title: <Text strong style={{ fontSize: 16 }}>Download the APK</Text>,
                description: (
                  <Space direction="vertical">
                    <Text>Download the APK directly on the device or transfer it from your computer:</Text>
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      href={OTA_APK_URL}
                      target="_blank"
                    >
                      Download APK
                    </Button>
                  </Space>
                ),
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Enable unknown sources</Text>,
                description: 'Go to Settings > Security > Unknown sources (or Install unknown apps) and enable it for your file manager or browser.',
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Install the APK</Text>,
                description: 'Open the downloaded APK file and tap Install.',
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Launch the app</Text>,
                description: 'Open MasterLapse and enter your view token.',
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const installItems = [
    {
      key: 'playstore',
      label: (
        <Space>
          <GoogleOutlined style={{ fontSize: 18 }} />
          <Text strong>Google Play Store</Text>
        </Space>
      ),
      children: (
        <div>
          <Alert
            type="warning"
            showIcon
            message="Beta Testing"
            description={<>The app is currently in beta testing on Google Play Store. Contact <a href="mailto:info@masterlapse.com">info@masterlapse.com</a> to request an invitation.</>}
            style={{ marginBottom: 16 }}
          />

          <Steps
            direction="vertical"
            size="small"
            current={-1}
            items={[
              {
                title: <Text strong style={{ fontSize: 16 }}>Request access</Text>,
                description: 'Contact us to be added as a tester with your Google account email.',
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Accept the invitation</Text>,
                description: 'You will receive an email with a link to join the internal test program.',
              },
              {
                title: <Text strong style={{ fontSize: 16 }}>Install from Play Store</Text>,
                description: 'After joining, the app will appear in your Play Store.',
              },
            ]}
          />

          <Alert
            type="info"
            showIcon
            icon={<MaterialIcon name="info" />}
            message="Automatic updates"
            description="The app will update automatically via Play Store. No manual intervention required."
            style={{ marginTop: 16 }}
          />
        </div>
      ),
    },
    {
      key: 'direct-apk',
      label: (
        <Space>
          <DownloadOutlined style={{ fontSize: 18 }} />
          <Text strong>Direct APK Install</Text>
          <Text type="secondary">(No Google account required)</Text>
        </Space>
      ),
      children: (
        <div>
          <Paragraph>
            Install the app directly via APK without requiring a Google account on each device.
            This is ideal for managing <Text strong>multiple devices</Text> (50+) without needing to create
            and manage separate Google accounts for each one.
          </Paragraph>

          <Paragraph>
            There are two installation methods depending on whether you want a <Text strong>Production Installation</Text> (kiosk lock) or a <Text strong>Test Installation</Text>:
          </Paragraph>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} md={12}>
              <Card size="small" title={<><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />Production Installation</>} style={{ height: '100%' }}>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li><Text strong>Device locked to app</Text> - Nobody can exit the app without authorization</li>
                  <li><Text strong>Boot protection</Text> - App starts after reboot (e.g. after a blackout)</li>
                  <li><Text strong>Auto permissions</Text> - Camera, storage granted automatically</li>
                  <li><Text strong>Remote app updates</Text> - No user action required</li>
                  <li><Text strong>Battery exempt</Text> - Android won't kill the app to save battery</li>
                </ul>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card size="small" title={<><CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />Test Installation</>} style={{ height: '100%' }}>
                <ul style={{ paddingLeft: 20, margin: 0 }}>
                  <li><Text type="secondary">Users can exit the app</Text></li>
                  <li><Text type="secondary">May not auto-start after reboot (depends on OEM)</Text></li>
                  <li><Text type="secondary">Permissions have to be granted manually</Text></li>
                  <li><Text type="secondary">Physical access needed for app updates</Text></li>
                  <li><Text type="secondary">Android may kill the app to save battery (depends on OEM)</Text></li>
                </ul>
              </Card>
            </Col>
          </Row>

          <Title level={5}>How to install</Title>

          <Collapse
            accordion
            items={otaSubItems}
          />
        </div>
      ),
    },
  ];

  return (
    <AppLayout>
      <div style={{ padding: '24px', maxWidth: 900 }}>
        <Title level={2}>
          <AndroidOutlined style={{ marginRight: 12 }} />
          Mobile App
        </Title>

        <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
          The MasterLapse mobile app turns any Android phone into a dedicated timelapse camera.
          Choose the installation method that best fits your use case.
        </Paragraph>

        <Card style={{ marginBottom: 24 }}>
          <Collapse
            accordion
            items={installItems}
            size="large"
          />
        </Card>

        <Divider />

        <Title level={4}>
          <MaterialIcon name="help" style={{ marginRight: 8 }} />
          Which method should I choose?
        </Title>

        <Card>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ padding: '12px 8px', textAlign: 'left' }}>Feature</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>Play Store</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>Production Installation</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>Test Installation</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Auto-start after reboot/blackout</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="warning">Depends on OEM</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="success">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="warning">Depends on OEM</Text>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Remote app updates</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="success">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="success">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="danger">No (tap required)</Text>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Battery optimization exempt</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="warning">Depends on OEM</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="success">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="warning">Depends on OEM</Text>
                </td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Device locked to app</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="success">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Google account required</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="danger">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 8px' }}>Factory reset required</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                  <Text type="warning">Yes</Text>
                </td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>No</td>
              </tr>
              <tr>
                <td style={{ padding: '12px 8px' }}>Setup time</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>3 min</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>10 min</td>
                <td style={{ padding: '12px 8px', textAlign: 'center' }}>3 min</td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>
    </AppLayout>
  );
}
